/**
 * 触屏控制：左下固定摇杆 + 右下动作按钮。
 * 只往 Input 的虚拟通道写状态，不直接碰世界/实体，玩法层零改动。
 *
 * 摇杆固定在画面左下角：底座不跟着手指跑（只有摇杆头动），热区就是底座周围那一小块，
 * 中间大片画面留给点按操作。按下点作为原点（相对摇杆手感），松开归位。
 */
const DEAD = 0.16;   // 死区，防手抖
const RADIUS = 52;   // 底座可拖动的最大半径（CSS px）

export function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
}

export class TouchControls {
  /**
   * @param {import('../core/input.js').Input} input
   * @param {{root?: HTMLElement, onAction?: (act: string) => void}} [opts]
   */
  constructor(input, opts = {}) {
    this.input = input;
    this.enabled = false;
    this.onAction = opts.onAction || null;   // 非按键动作（如存档）回调
    this.stickId = null;          // 当前摇杆 pointerId
    this.origin = { x: 0, y: 0 }; // 底座中心（视口坐标）

    this.root = opts.root || document.getElementById('touch');
    if (!this.root) return;

    this.zone = this.root.querySelector('#stick-zone');
    this.base = this.root.querySelector('#stick-base');
    this.knob = this.root.querySelector('#stick-knob');

    this._bindStick();
    this._bindButtons();
    this.bindLifecycle();
  }

  /** 摇杆事件（Pointer Events 同时覆盖鼠标与触摸） */
  _bindStick() {
    if (!this.zone) return;

    const start = (e) => {
      if (!this.enabled) return;
      // 上一根手指没抬干净（pointerup 丢了）就先收尾，免得摇杆彻底失灵
      if (this.stickId !== null) this.releaseStick();
      this.stickId = e.pointerId;
      this.origin = { x: e.clientX, y: e.clientY };   // 底座固定不动，只以按下点为原点
      this.base.classList.add('active');
      this.zone.setPointerCapture && this.zone.setPointerCapture(e.pointerId);
      this._move(e);
      e.preventDefault();
    };

    const move = (e) => {
      if (e.pointerId !== this.stickId) return;
      this._move(e);
      e.preventDefault();
    };

    const end = (e) => {
      if (e.pointerId !== this.stickId) return;
      this.releaseStick();
      e.preventDefault();
    };

    this.zone.addEventListener('pointerdown', start, { passive: false });
    this.zone.addEventListener('pointermove', move, { passive: false });
    this.zone.addEventListener('pointerup', end, { passive: false });
    this.zone.addEventListener('pointercancel', end, { passive: false });
  }

  _move(e) {
    let dx = e.clientX - this.origin.x;
    let dy = e.clientY - this.origin.y;
    const len = Math.hypot(dx, dy);
    if (len > RADIUS) { dx = dx / len * RADIUS; dy = dy / len * RADIUS; }

    this.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;

    // 归一化到 [-1, 1]，再过死区
    let nx = dx / RADIUS, ny = dy / RADIUS;
    if (Math.hypot(nx, ny) < DEAD) { this.input.setAxis(0, 0); return; }
    this.input.setAxis(nx, ny);
  }

  /** 动作按钮：data-key 指定要按的键，data-hold="1" 表示按住有效 */
  _bindButtons() {
    for (const btn of this.root.querySelectorAll('[data-key]')) {
      const key = btn.dataset.key;
      const hold = btn.dataset.hold === '1';

      const down = (e) => {
        if (!this.enabled) return;
        btn.classList.add('on');
        if (hold) this.input.virtualDown(key);
        else this.input.virtualTap(key);
        btn.setPointerCapture && btn.setPointerCapture(e.pointerId);
        e.preventDefault();
        e.stopPropagation();
      };
      const up = (e) => {
        btn.classList.remove('on');
        if (hold) this.input.virtualUp(key);
        e && e.preventDefault();
        e && e.stopPropagation();
      };

      // 不用 pointerleave：指针捕获后手指轻微滑出按钮边界会误松开，改由 window 级兜底
      btn.addEventListener('pointerdown', down, { passive: false });
      btn.addEventListener('pointerup', up, { passive: false });
      btn.addEventListener('pointercancel', up, { passive: false });
      btn.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    // 非按键动作（存档之类）：不进输入通道，直接回调给 main.js 接线
    for (const btn of this.root.querySelectorAll('[data-act]')) {
      const act = btn.dataset.act;
      const fire = (e) => {
        if (!this.enabled) return;
        btn.classList.add('on');
        setTimeout(() => btn.classList.remove('on'), 200);
        if (this.onAction) this.onAction(act);
        e.preventDefault();
        e.stopPropagation();
      };
      btn.addEventListener('pointerdown', fire, { passive: false });
      btn.addEventListener('contextmenu', (e) => e.preventDefault());
    }
  }

  /** 双击摇杆之外的画面 = 交互（E），方便单手点物 */
  bindLifecycle() {
    // iOS 双指/双击缩放会打断摇杆，这里统一禁掉
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    // 手指在按钮外抬起也要松开（摇杆同理，避免抬手后角色还在走）
    window.addEventListener('pointerup', (e) => {
      this.releaseHoldButtons();
      if (this.stickId !== null && e.pointerId === this.stickId) this.releaseStick();
    });
    window.addEventListener('pointercancel', () => this.releaseAll());
    // 页面切到后台时清空输入，避免回来后角色一直走
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.releaseAll();
    });
    window.addEventListener('blur', () => this.releaseAll());
  }

  releaseHoldButtons() {
    if (!this.root) return;
    for (const btn of this.root.querySelectorAll('[data-key][data-hold="1"]')) {
      this.input.virtualUp(btn.dataset.key);
      btn.classList.remove('on');
    }
  }

  /** 摇杆归位：底座留在左下角，方向清零 */
  releaseStick() {
    this.stickId = null;
    if (this.base) this.base.classList.remove('active');
    if (this.knob) this.knob.style.transform = 'translate(-50%, -50%)';
    this.input.setAxis(0, 0);
  }

  releaseAll() {
    this.releaseStick();
    this.releaseHoldButtons();
  }

  /** 顶栏「摇杆」按钮用：开 / 关 */
  toggle(force) {
    const next = force === undefined ? !this.enabled : !!force;
    next ? this.enable() : this.disable();
    return this.enabled;
  }

  /** 由 main.js 在触摸设备上调用 */
  enable() {
    this.enabled = true;
    this.root && this.root.classList.remove('hidden');
    document.body.classList.add('is-touch');
  }

  disable() {
    this.enabled = false;
    this.releaseAll();
    this.root && this.root.classList.add('hidden');
    document.body.classList.remove('is-touch');
  }
}
